/**
 * ConditionReportButton — "View Real Estate Condition Report" on a listing.
 *
 * Wisconsin sellers sign their condition report (RECR, or the Vacant Land
 * Disclosure Report) before the listing goes live, so buyers can read it
 * before they make an offer. The listing says whether one is available:
 *
 *     condition_report: { available, form: "RECR" | "VLDR", title }
 *
 * Signed in: the PDF is fetched with the buyer's session
 * (GET /api/houses/{id}/disclosure/report) and opened in a new tab. A browser
 * navigation can't carry the API's cookie auth through the dev proxy, so it
 * is fetched as a Blob and shown from an object URL.
 *
 * Signed out: `onRequireSignIn` is called, and the page asks the visitor to
 * sign in ("Sign in to view the seller's condition report.").
 */

import React, { useEffect, useRef, useState } from 'react';
import { DisclosureAPI } from '../../agents/disclosureAgent';
import styles from './ConditionReportButton.module.css';

/** Report titles when the listing doesn't name its report. */
const FORM_TITLES = {
  RECR: 'Real Estate Condition Report',
  VLDR: 'Vacant Land Disclosure Report',
};

/** The sign-in note when the listing doesn't name its report. */
export const SIGN_IN_NOTE = "Sign in to view the seller's condition report.";

/**
 * "Sign in to view the seller's Vacant Land Disclosure Report." — the report's
 * own title (legal review round 2, §2.3), else the generic note.
 */
export function signInNote(report) {
  const title = report?.title && String(report.title).trim();
  return title ? `Sign in to view the seller's ${title}.` : SIGN_IN_NOTE;
}

/** How long an opened report's object URL is kept before it is released. */
const OBJECT_URL_TTL_MS = 60000;

/** "Real Estate Condition Report", from the listing's `condition_report`. */
export function conditionReportTitle(report) {
  return report?.title || FORM_TITLES[report?.form] || 'Condition Report';
}

/** True when the listing offers a condition report to buyers. */
export const hasConditionReport = (report) => Boolean(report?.available);

function errorMessageFor(err) {
  const status = err?.status ?? err?.statusCode;
  if (status === 404) return "The seller's condition report isn't available right now.";
  // The signed copy isn't ready yet (e.g. DocuSign is still finishing it).
  if (status === 503) return "The signed report isn't available right now. Please try again shortly.";
  if (status === 401) return 'Your session has ended. Sign in again to view the report.';
  if (status === 403) return "You don't have access to this report.";
  return err?.message || "We couldn't open the report. Please try again.";
}

/**
 * @param {object} props
 * @param {number} props.houseId
 * @param {{available: boolean, form?: string, title?: string}} props.conditionReport
 * @param {boolean} props.signedIn
 * @param {() => void} [props.onRequireSignIn] - signed out: ask them to sign in
 * @param {string} [props.buttonClassName] - the host page's button style
 */
export default function ConditionReportButton({
  houseId,
  conditionReport,
  signedIn,
  onRequireSignIn,
  buttonClassName,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const urlsRef = useRef([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
  }, []);

  if (!hasConditionReport(conditionReport)) return null;

  const title = conditionReportTitle(conditionReport);

  const openReport = async () => {
    if (loading) return;
    if (!signedIn) {
      if (typeof onRequireSignIn === 'function') onRequireSignIn();
      return;
    }
    setError(null);
    setLoading(true);
    // Opened now, while the click still counts as a user gesture; popup
    // blockers refuse a window opened after the fetch.
    const tab = typeof window.open === 'function' ? window.open('', '_blank') : null;
    if (tab) {
      try {
        tab.opener = null;
      } catch (_) {
        /* some browsers make opener read-only */
      }
    }
    try {
      const { blob } = await DisclosureAPI.getConditionReport(houseId);
      const pdf = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdf);
      urlsRef.current.push(url);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        urlsRef.current = urlsRef.current.filter((u) => u !== url);
      }, OBJECT_URL_TTL_MS);
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup blocked: a link click opens it in a tab instead.
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      if (tab) tab.close();
      console.error('Condition report fetch failed:', err?.status);
      if (mountedRef.current) setError(errorMessageFor(err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <div className={styles.wrap} data-testid="condition-report">
      <button
        type="button"
        className={buttonClassName || styles.button}
        onClick={openReport}
        disabled={loading}
        aria-busy={loading || undefined}
        data-testid="condition-report-button"
      >
        {loading ? `Opening ${title}…` : `View ${title}`}
      </button>
      {error && (
        <p className={styles.error} role="alert" data-testid="condition-report-error">
          {error}
        </p>
      )}
    </div>
  );
}
